import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import {
  createPlannedCourse,
  createPlannedLesson,
  createPlannedModule,
  nextPlannedLessonId,
} from '@platform/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';

import { STORY_HELPER, STORY_TEMPLATE } from './ModuleStory';

import type { PlannedCourse } from '@platform/schema/curriculum';
import type { ReactNode } from 'react';

export type Adding = 'course' | 'module' | 'lesson';

const TITLES: Record<Adding, string> = {
  course: 'Add a planned course',
  module: 'Add a planned module',
  lesson: 'Add a planned lesson',
};

const NO_LESSON = {
  name: '',
  description: '',
  writerBrief: '',
  primarySourceName: '',
  primarySourceUrl: '',
  additionalSourceName: '',
  additionalSourceUrl: '',
};

/**
 * Add a course, module or lesson to the plan — spec §5. Each lands last in its parent and
 * is a planning row only: nothing appears under Courses, Modules or Lessons until it is
 * built.
 */
export function AddDialog({
  adding,
  courses,
  onClose,
  onAdded,
}: {
  adding: Adding | null;
  courses: readonly PlannedCourse[];
  onClose: () => void;
  onAdded: (message: string, lessonId?: string) => void;
}) {
  const queries = useQueryClient();
  const [name, setName] = useState('');
  const [story, setStory] = useState(STORY_TEMPLATE);
  const [courseId, setCourseId] = useState('');
  const [moduleId, setModuleId] = useState('');
  const [lesson, setLesson] = useState(NO_LESSON);
  // Null until the admin types an ID: the next free one fills the field until then.
  const [typedId, setTypedId] = useState<string | null>(null);

  const modules =
    courses.find((course) => course.id === courseId)?.modules ?? [];

  const suggested = useQuery({
    queryKey: ['curriculum', 'next-lesson-id', moduleId],
    enabled: adding === 'lesson' && moduleId !== '',
    queryFn: async () =>
      (
        await nextPlannedLessonId({
          path: { id: moduleId },
          throwOnError: true,
        })
      ).data.lessonId,
  });

  const close = () => {
    setName('');
    setStory(STORY_TEMPLATE);
    setCourseId('');
    setModuleId('');
    setLesson(NO_LESSON);
    setTypedId(null);
    save.reset();
    onClose();
  };

  const save = useTrackedMutation(adding === null ? 'Add' : TITLES[adding], {
    mutationFn: async (): Promise<{ message: string; lessonId?: string }> => {
      if (adding === 'course') {
        const { data } = await createPlannedCourse({
          body: { name },
          throwOnError: true,
        });
        return { message: `Added the planned course ${data.name}.` };
      }
      if (adding === 'module') {
        const { data } = await createPlannedModule({
          body: { courseId, name, story },
          throwOnError: true,
        });
        return { message: `Added the planned module ${data.name}.` };
      }
      const wanted =
        typedId === null ? suggested.data : typedId.trim() || undefined;
      const { data } = await createPlannedLesson({
        body: {
          moduleId,
          ...(wanted !== undefined && { lessonId: wanted }),
          ...lesson,
        },
        throwOnError: true,
      });
      return {
        message: `Added the planned lesson ${data.lessonId} — ${data.name}.`,
        lessonId: data.lessonId,
      };
    },
    onSuccess: async ({ message, lessonId }) => {
      await queries.invalidateQueries({ queryKey: ['curriculum'] });
      close();
      onAdded(message, lessonId);
    },
  });

  const ready =
    adding === 'course'
      ? name.trim() !== ''
      : adding === 'module'
        ? courseId !== '' &&
          name.trim() !== '' &&
          story.trim() !== '' &&
          story.trim() !== STORY_TEMPLATE.trim()
        : moduleId !== '' && lesson.name.trim() !== '';

  const field = (key: keyof typeof NO_LESSON, label: string, extra = {}) => (
    <TextField
      size="small"
      label={label}
      value={lesson[key]}
      onChange={(event) => {
        setLesson({ ...lesson, [key]: event.target.value });
      }}
      {...extra}
    />
  );

  let body: ReactNode = null;
  if (adding === 'course') {
    body = (
      <TextField
        autoFocus
        size="small"
        label="Course name"
        value={name}
        onChange={(event) => {
          setName(event.target.value);
        }}
      />
    );
  } else if (adding !== null) {
    body = (
      <>
        <TextField
          select
          size="small"
          label="Course"
          value={courseId}
          onChange={(event) => {
            setCourseId(event.target.value);
            setModuleId('');
          }}
        >
          {courses.map((course) => (
            <MenuItem key={course.id} value={course.id}>
              {course.name}
            </MenuItem>
          ))}
        </TextField>
        {adding === 'module' ? (
          <>
            <TextField
              size="small"
              label="Module name"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
            <TextField
              required
              multiline
              minRows={10}
              size="small"
              label="Module story"
              helperText={STORY_HELPER}
              value={story}
              onChange={(event) => {
                setStory(event.target.value);
              }}
            />
          </>
        ) : (
          <>
            <TextField
              select
              size="small"
              label="Module"
              value={moduleId}
              disabled={courseId === ''}
              helperText="The lesson goes last in this module."
              onChange={(event) => {
                setModuleId(event.target.value);
              }}
            >
              {modules.map((module) => (
                <MenuItem key={module.id} value={module.id}>
                  {module.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Lesson ID"
              value={typedId ?? suggested.data ?? ''}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText={
                moduleId === ''
                  ? 'Pick a module to get the next free ID.'
                  : 'The next free ID in this module. Change it to any unused ID.'
              }
              onChange={(event) => {
                setTypedId(event.target.value);
              }}
            />
            {field('name', 'Lesson name')}
            {field('description', 'Description', {
              multiline: true,
              minRows: 2,
            })}
            {field('writerBrief', 'Writer brief', {
              multiline: true,
              minRows: 3,
            })}
            {field('primarySourceName', 'Primary source name')}
            {field('primarySourceUrl', 'Primary source URL')}
            {field('additionalSourceName', 'Additional source name')}
            {field('additionalSourceUrl', 'Additional source URL')}
          </>
        )}
      </>
    );
  }

  return (
    <Dialog open={adding !== null} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{adding === null ? '' : TITLES[adding]}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {body}
          {save.isError && (
            <Alert severity="error">{describeApiError(save.error)}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!ready || save.isPending}
          onClick={() => {
            save.mutate();
          }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
